import { type AuditLightingCountFormEntry } from '#packages/data-collection/models/audit-lighting-count-form';
import { type AuditLightingTypeFormEntry } from '#packages/data-collection/models/audit-lighting-type-form';

import {
  CeilingHeightLabel,
  SurfaceTypeLabel,
  ControlsLabel,
} from '#apps/client-web/features/audit-form-lighting-count/constants';

import { type ExcelTableMapping } from '../types/excel-mapping';

/**
 * Source data for a single Lighting Count row.
 * Includes all lighting type entries so that `fixtureType` IDs
 * can be resolved to their letter index (A, B, C, …).
 */
export type LightingCountExportEntry = {
  entry: AuditLightingCountFormEntry;
  lightingTypeEntries: ReadonlyArray<AuditLightingTypeFormEntry>;
};

/**
 * Resolves a fixtureType ID to the corresponding letter index
 * used in the "2. Lighting types" sheet (A, B, C, …).
 */
const resolveFixtureTypeLetter = (
  fixtureTypeId: string | undefined,
  lightingTypeEntries: ReadonlyArray<AuditLightingTypeFormEntry>,
): string | undefined => {
  if (!fixtureTypeId) return undefined;
  const index = lightingTypeEntries.findIndex((e) => e.id === fixtureTypeId);
  return index >= 0 ? String.fromCharCode(65 + index) : undefined;
};

/**
 * Maps Lighting Count form entries to the "2. Lighting count" Excel sheet.
 *
 * Each entry occupies one row starting at row 10.
 * Column D (Luminaire) is a formula in the template — we skip it.
 */
export const lightingCountTableMapping: ExcelTableMapping<LightingCountExportEntry> = {
  label: 'Lighting Count',
  startRangeName: 'lighting_count_data_start',

  columns: [
    /* A – Section */
    {
      column: 'A',
      getValue: (d) => d.entry.section,
    },
    /* B – Room */
    {
      column: 'B',
      getValue: (d) => d.entry.room,
    },
    /* C – Fixture type (letter reference to Lighting Types sheet) */
    {
      column: 'C',
      getValue: (d) =>
        resolveFixtureTypeLetter(d.entry.fixtureType, d.lightingTypeEntries),
    },
    /* D – Luminaire (formula — skip) */
    /* E – Fix. qty */
    {
      column: 'E',
      getValue: (d) => d.entry.fixtureQuantity,
    },
    /* F – Ceiling height (ft) */
    {
      column: 'F',
      getValue: (d) =>
        d.entry.ceilingHeight ? CeilingHeightLabel[d.entry.ceilingHeight] : undefined,
    },
    /* G – Surface type */
    {
      column: 'G',
      getValue: (d) =>
        d.entry.surfaceType ? SurfaceTypeLabel[d.entry.surfaceType] : undefined,
    },
    /* H – Controls */
    {
      column: 'H',
      getValue: (d) =>
        d.entry.controls ? ControlsLabel[d.entry.controls] : undefined,
    },
    /* I – Hrs/day of operation */
    {
      column: 'I',
      getValue: (d) => d.entry.operationHoursPerDay,
    },
    /* J – Days/wk of operation */
    {
      column: 'J',
      getValue: (d) => d.entry.operationDaysPerWeek,
    },
    /* K – Comments */
    {
      column: 'K',
      getValue: (d) => d.entry.comments,
    },
  ],
};
